import { v4 as uuid } from 'uuid'

import api from '@/api/fetch'
import StorageFactory from '@/drivers/storage'
import { AdyenTerminalApiPlugin } from '@/integrations/adyen/AdyenTerminalApiPlugin'
import { logger } from '@/monitoring'
import { useConfigStore } from '@/store/config'
import type { Payment } from '@/types'

import { type IDataphone } from './dataphone'

type AdyenConfig = {
  poiid: string
}

type AdyenPayment = Payment & {
  metadata: {
    tenderReference: string
    pspReference: string
  }
}

type AdyenCredentials = {
  username: string
  password: string
}

export class AdyenDataphone implements IDataphone {
  public initialized: boolean = false
  public canCancel: boolean = true

  private config: AdyenConfig
  //private credentials: AdyenCredentials | undefined

  constructor(config: AdyenConfig) {
    this.initialized = false
    this.config = config
  }

  private async generateCredentials(): Promise<AdyenCredentials | null> {
    try {
      const username = uuid()
      const password = uuid()
      await api.post('pos/adyen/credentials', {
        username,
        password,
        poiid: this.config.poiid
      })
      return { username, password }
    } catch (e) {
      logger.error('Failed to generate Adyen credentials', e as Error)
    }
    return null
  }

  private async getAuthCredentials(): Promise<AdyenCredentials | null> {
    const storedCredentials =
      await StorageFactory.getStorage().getItem<AdyenCredentials>('adyenCredentials')
    if (storedCredentials) {
      return storedCredentials
    }
    const credentials = await this.generateCredentials()
    await StorageFactory.getStorage().setItem('adyenCredentials', credentials)
    return credentials
  }

  async init() {
    /* const credentials = await this.getAuthCredentials()
    if (!credentials) {
      return false
    }
    this.credentials = credentials */
    this.initialized = true
    return true
  }

  async charge({ amount, tabId, paymentId }: { amount: number; tabId: string; paymentId: string }) {
    const configStore = useConfigStore()
    let response
    try {
      const isDataphone = configStore.currentAdyenDataphone
      if (isDataphone) {
        response = await AdyenTerminalApiPlugin.charge({
          paymentId,
          amount,
          tabId,
          poiid: configStore.currentAdyenDataphone!.poiid
        })
      } else {
        response = (
          await api.post('pos/adyen/payment', {
            amount,
            tabId,
            paymentId,
            poiid: this.config.poiid
          })
        ).data
      }
      return response
    } catch (e: any) {
      throw new Error(e.message)
    }
  }

  async cancel({ paymentId, tabId }: { paymentId: string; tabId: string }) {
    try {
      const response = await api.put('pos/adyen/cancelPayment', {
        paymentId,
        tabId,
        poiid: this.config.poiid
      })
      return response.data
    } catch (e: any) {
      throw new Error(e.response.data)
    }
  }

  async refund(payment: AdyenPayment, tabId: string) {
    const response = await api.post('pos/adyen/refund', {
      tenderReference: payment.metadata.tenderReference,
      pspReference: payment.metadata.pspReference,
      amount: payment.amount,
      tabId,
      poiid: this.config.poiid
    })
    return response.data
  }
}
